import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Children, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

interface SectionRailProps {
  title: string;
  subtitle?: string;
  /** Renders a "Xem tất cả →" link on the right of the header. */
  viewAllHref?: string;
  viewAllLabel?: string;
  /** Applied to the <li> wrapping each child — set the card width here. */
  itemClassName?: string;
  /** Track gap. Kept as a class so the page step can measure the real columnGap. */
  gapClassName?: string;
  /** Let the track bleed to the viewport edge on mobile. */
  bleed?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * The shared discovery rail: section header + horizontally scrolling track.
 *
 * Arrow state is *derived* from measured scroll extents rather than assumed, so a
 * rail whose content already fits shows no arrows at all. A ResizeObserver watches
 * the track and its children, which is what keeps the arrows correct once lazily
 * loaded images resolve and change the track width.
 */
export function SectionRail({
  title,
  subtitle,
  viewAllHref,
  viewAllLabel = 'Xem tất cả',
  itemClassName,
  gapClassName = 'gap-3 md:gap-4',
  bleed = true,
  className,
  children,
}: SectionRailProps) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const childCount = Children.count(children);

  const sync = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 1);
    setCanNext(max > 1 && el.scrollLeft < max - 1);
  }, []);

  useEffect(() => {
    sync();
    const el = trackRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(sync);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [sync, childCount]);

  const scrollByPage = (direction: 'prev' | 'next') => {
    const el = trackRef.current;
    if (!el) return;

    const first = el.firstElementChild as HTMLElement | null;
    const gap = Number.parseFloat(window.getComputedStyle(el).columnGap) || 0;
    const card = first ? first.offsetWidth + gap : 0;
    // Advance by whole cards so the track never comes to rest mid-card.
    const step = card > 0 ? Math.max(card, Math.floor(el.clientWidth / card) * card) : el.clientWidth * 0.8;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({ left: direction === 'prev' ? -step : step, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  const scrollable = canPrev || canNext;

  return (
    <section className={cn('relative', className)} aria-labelledby={`rail-${title}`}>
      <header className="mb-4 flex items-end justify-between gap-4 md:mb-5">
        <div className="flex min-w-0 items-center gap-3">
          <span
            aria-hidden="true"
            className="block h-6 w-[3px] shrink-0 rounded-full"
            style={{ backgroundColor: 'var(--section-accent)' }}
          />
          <div className="min-w-0">
            <h2 id={`rail-${title}`} className="truncate text-xl leading-tight font-bold text-gray-900 md:text-2xl">
              {title}
            </h2>
            {subtitle && <p className="mt-0.5 truncate text-sm text-gray-500">{subtitle}</p>}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {viewAllHref && (
            <Link
              href={viewAllHref}
              className="text-primary hover:text-primary/80 hidden items-center gap-0.5 text-sm font-medium transition-colors sm:inline-flex"
            >
              {viewAllLabel}
              <ChevronRight className="size-4" />
            </Link>
          )}
          {scrollable && (
            <div className="hidden items-center gap-2 md:flex">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="size-9 rounded-full"
                onClick={() => scrollByPage('prev')}
                disabled={!canPrev}
                aria-label={`${title} — lùi lại`}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="size-9 rounded-full"
                onClick={() => scrollByPage('next')}
                disabled={!canNext}
                aria-label={`${title} — tiếp theo`}
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}
        </div>
      </header>

      <ul
        ref={trackRef}
        onScroll={sync}
        role="list"
        className={cn(
          'scrollbar-hide flex overflow-x-auto overscroll-x-contain pb-1',
          gapClassName,
          // Mandatory snap fights touch momentum, so snap only from md up.
          'snap-none scroll-pl-4 md:snap-x md:snap-mandatory md:scroll-pl-0',
          bleed && '-mx-4 px-4 md:mx-0 md:px-0',
        )}
      >
        {Children.map(children, (child, index) => (
          <li key={index} className={cn('shrink-0 snap-start', itemClassName)}>
            {child}
          </li>
        ))}
        {/* Safari drops trailing padding on flex scroll containers. */}
        {bleed && <li aria-hidden="true" className="w-px shrink-0 md:hidden" />}
      </ul>

      {viewAllHref && (
        <Link
          href={viewAllHref}
          className="text-primary hover:text-primary/80 mt-4 inline-flex items-center gap-0.5 text-sm font-medium sm:hidden"
        >
          {viewAllLabel}
          <ChevronRight className="size-4" />
        </Link>
      )}
    </section>
  );
}
